"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";
import { colorMapping } from "./util-color";
import { formatClusterPattern } from "./util-filter";
import {
  groupSymbolsBySector,
  sortSymbolsByStrength,
  sortGroupsByStrength,
  sortGroupsBySector,
  sortSectorSymbolsByPercentChange,
  filterSymbolsByMarketCap,
  filterSymbolsByPercentageThreshold,
  filterIdenticalSymbolGroups,
} from "./util-symbol-grouping";
import KVChildRow from "./KVChildRow";
import { upsertSectorSignal as upsertSectorSignalApi } from "../services/supabase-api";
import { Toast } from "./Toast";

const KVRow = observer(function KVRow({
  k,
  v,
  colCh,
  isPos,
  symbols,
  open,
  onToggle,
  childOpenMap,
  onToggleChild,
  sectorSignal,
  onSectorSignalChange,
}) {
  const store = useStore();
  const [toastMsg, setToastMsg] = React.useState(null);
  const [toastType, setToastType] = React.useState("success");
  const displayKey = (() => {
    const lastPart = k.includes("-") ? k.split("-").pop() : k;
    return lastPart.length > colCh
      ? lastPart.slice(0, colCh - 1) + "…"
      : lastPart;
  })();
  const color = colorMapping(k) || "inherit";
  const hasChildren = !!(symbols && Object.keys(symbols).length);
  const hasTrailingCount = /\s\d+\s*$/.test(String(v || ""));
  const signalOrderType = sectorSignal?.order_type;
  const signalColor =
    signalOrderType === 1
      ? "rgba(34,197,94,.9)"
      : signalOrderType === -1
        ? "rgba(239,68,68,.9)"
        : null;

  const closeToast = () => setToastMsg(null);

  const saveSectorSignal = async (orderType) => {
    const { error } = await upsertSectorSignalApi({
      sector: k,
      orderType,
      signalStrength: 5,
      date: store.formattedDate,
    });

    if (error) {
      setToastType("error");
      //console.log("AAA", error, error.message)
      setToastMsg(error.message || "Save failed");
      return;
    }

    if (onSectorSignalChange) {
      onSectorSignalChange(k, orderType);
    }
    setToastType("success");
    setToastMsg(`${orderType === 1 ? "Buy" : "Sell"} ${k.toUpperCase()} added`);
  };

  const handleBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    saveSectorSignal(1);
  };

  const handleSell = (e) => {
    e.preventDefault();
    e.stopPropagation();
    saveSectorSignal(-1);
  };

  return (
    <div style={{ marginBottom: 0 }}>
      {/* Row header */}
      <div
        role={hasChildren ? "button" : "link"}
        tabIndex={0}
        onClick={() => onToggle(k, hasChildren)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(k, hasChildren);
          }
        }}
        title={k}
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: `${colCh}ch`,
                fontSize: 16,
                display: "inline-block",
                backgroundColor: color,
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.1)",
                flex: "0 0 auto",
                textAlign: "center",
                paddingTop: 2,
                paddingBottom: 2,
                color: "black",
              }}
            >
              {displayKey.toUpperCase()}
            </span>
            {signalColor && (
              <span
                title={`Sector ${signalOrderType === 1 ? "Buy" : "Sell"}`}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: signalColor,
                  boxShadow: "0 0 0 2px rgba(0,0,0,.4)",
                  flex: "0 0 auto",
                }}
              />
            )}
          </span>
          <span
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
              backgroundColor: isPos
                ? "rgba(34,197,94,.1)"
                : "rgba(239,68,68,.1)",
              color: "#e5e7eb",
              borderRadius: 4,
              padding: "2px 6px",
            }}
          >
            {/*!store.isPercent ? formatClusterPattern(v) : v.split(" ")[0]*/}
            <span style={{ color: hasTrailingCount ? "#FFD166" : "inherit" }}>
              {formatClusterPattern(v)}
            </span>

            {store.isAdmin &&
              store.isAdminParam &&
              store.allowSignal &&
              isPos && (
                <span
                  onClick={handleBuy}
                  title="Buy sector"
                  style={{
                    fontSize: 12,
                    lineHeight: 1,
                    opacity: 0.55,
                    cursor: "pointer",
                    userSelect: "none",
                    color: "rgba(34,197,94,.95)",
                    paddingLeft: 6,
                  }}
                >
                  B
                </span>
              )}

            {store.isAdmin &&
              store.isAdminParam &&
              store.allowSignal &&
              !isPos && (
                <span
                  onClick={handleSell}
                  title="Sell sector"
                  style={{
                    fontSize: 12,
                    lineHeight: 1,
                    opacity: 0.55,
                    cursor: "pointer",
                    userSelect: "none",
                    color: "rgba(239,68,68,.95)",
                    paddingLeft: 6,
                  }}
                >
                  S
                </span>
              )}
          </span>
        </div>
        {hasChildren && (
          <svg
            aria-hidden
            width="16"
            height="16"
            viewBox="0 0 24 24"
            style={{
              marginLeft: 6,
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 120ms ease",
              opacity: 0.6,
              flex: "0 0 auto",
            }}
          >
            <path
              d="M9 6l6 6-6 6"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {/* Dropdown with child rows */}
      {open && hasChildren && (
        <div
          style={{
            marginTop: 2,
            padding: 4,
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 6,
            background: isPos ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)",
            color: "#f3f4f6",
          }}
        >
          {/* Symbol rows */}
          {(() => {
            const rawSymbolEntries = Object.entries(symbols);

            // Apply percentage filter if enabled
            const percentageFilteredEntries = store.filterByPercentageThreshold
              ? filterSymbolsByPercentageThreshold(
                  rawSymbolEntries,
                  store.currentGridMap,
                  store.formattedDate,
                  5,
                )
              : rawSymbolEntries;
            const symbolEntries = store.isMarketCapFilter
              ? filterSymbolsByMarketCap(
                  percentageFilteredEntries,
                  store.marketCapMap,
                )
              : percentageFilteredEntries;

            if (!store.sectorSymbolsSortByStrength) {
              // Check if we should sort by percent change
              if (store.isPercent && store.strongSort) {
                // For positive moves: descending (largest gains first)
                // For negative moves: ascending (most negative first)
                const sortedEntries = sortSectorSymbolsByPercentChange(
                  symbolEntries,
                  store.currentGridMap,
                  store.formattedDate,
                  isPos, // isPos determines sort direction
                );
                return sortedEntries.map(([sym, pat]) => (
                  <KVChildRow
                    key={sym}
                    sym={sym}
                    pat={pat}
                    open={!!childOpenMap[sym]}
                    onToggle={onToggleChild}
                    colCh={10}
                    isPos={isPos}
                  />
                ));
              } else {
                // Sort by strength using utility function
                const sortedEntries = sortSymbolsByStrength(symbolEntries);
                return sortedEntries.map(([sym, pat]) => (
                  <KVChildRow
                    key={sym}
                    sym={sym}
                    pat={pat}
                    open={!!childOpenMap[sym]}
                    onToggle={onToggleChild}
                    colCh={10}
                    isPos={isPos}
                  />
                ));
              }
            } else {
              // Group by sector with spacing between groups
              const grouped = groupSymbolsBySector(symbolEntries);
              const sortedGroupEntries = store.sectorSymbolsSortBySector
                ? sortGroupsBySector(
                    grouped,
                    store.currentGridMap,
                    store.formattedDate,
                    isPos,
                  )
                : sortGroupsByStrength(
                    grouped,
                    store.currentGridMap,
                    store.formattedDate,
                  isPos,
                );
              const groupEntries = filterIdenticalSymbolGroups(sortedGroupEntries);

              return groupEntries.map(
                ([sectorKey, sectorSymbols], groupIndex) => (
                  <div key={sectorKey}>
                    {groupIndex > 0 && <div style={{ height: 8 }} />}
                    {/* Group name header */}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.6)",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: 4,
                        paddingLeft: 4,
                        borderLeft: "2px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      {sectorKey}
                    </div>
                    {(() => {
                      // Sort symbols within group by percentage change if isPercent and strongSort, otherwise keep original order
                      const orderedSymbols =
                        store.isPercent && store.strongSort
                          ? sortSectorSymbolsByPercentChange(
                              sectorSymbols,
                              store.currentGridMap,
                              store.formattedDate,
                              isPos, // isPos determines sort direction
                            )
                          : sectorSymbols;

                      return orderedSymbols.map(([sym, pat]) => (
                        <KVChildRow
                          key={sym}
                          sym={sym}
                          pat={pat}
                          open={!!childOpenMap[sym]}
                          onToggle={onToggleChild}
                          colCh={10}
                          isPos={isPos}
                        />
                      ));
                    })()}
                  </div>
                ),
              );
            }
          })()}
        </div>
      )}

      {toastMsg && (
        <Toast
          message={toastMsg}
          type={toastType}
          onClose={closeToast}
          duration={1500}
        />
      )}
    </div>
  );
});

export default KVRow;
